import { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Score gauge component
const ScoreGauge = ({ score, label }) => {
  const getColor = (s) => {
    if (s >= 80) return 'text-green-500';
    if (s >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getBgColor = (s) => {
    if (s >= 80) return 'bg-green-500';
    if (s >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="text-center">
      <div className="relative w-24 h-24 mx-auto mb-2">
        <svg className="w-24 h-24 transform -rotate-90">
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            className="text-gray-200"
          />
          <circle
            cx="48"
            cy="48"
            r="40"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            strokeDasharray={251.2}
            strokeDashoffset={251.2 - (251.2 * score) / 100}
            className={getBgColor(score)}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-2xl font-bold ${getColor(score)}`}>{score}</span>
        </div>
      </div>
      <p className="text-sm text-gray-600">{label}</p>
    </div>
  );
};

ScoreGauge.propTypes = {
  score: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired
};

// Keyword badge component
const KeywordBadge = ({ keyword, type }) => {
  const colors = {
    matched: 'bg-green-100 text-green-800 border-green-200',
    missing: 'bg-red-100 text-red-800 border-red-200'
  };

  return (
    <span className={`inline-block px-3 py-1 text-sm rounded-full border ${colors[type]} mr-2 mb-2`}>
      {keyword}
    </span>
  );
};

KeywordBadge.propTypes = {
  keyword: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['matched', 'missing']).isRequired
};

const ATSCheckerPage = () => {
  const [resumeText, setResumeText] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);

    if (resumeText.trim().length < 100) {
      setError('Please enter your resume text (minimum 100 characters)');
      return;
    }

    if (jobDescription.trim().length < 50) {
      setError('Please enter a job description (minimum 50 characters)');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/public/ats-check`, {
        resumeText,
        jobDescription
      });

      if (response.data.success) {
        setResult(response.data.data);
        const { track } = await import('../utils/analytics').then(m => ({ track: m.analytics.track }));
        track('ats_check_used', { score: response.data.data?.score });
      } else {
        setError(response.data.error || 'Analysis failed');
      }
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to analyze. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary-900 to-slate-900">
      {/* Header */}
      <nav className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <span className="text-primary-600 font-bold text-xl">K</span>
            </div>
            <span className="text-white font-semibold text-xl">Kwikly</span>
          </a>
          <div className="flex items-center gap-4">
            <a
              href="/login"
              className="text-white/80 hover:text-white transition-colors"
            >
              Sign In
            </a>
            <a
              href="/register"
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
            >
              Get Started Free
            </a>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center text-white mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            Free ATS Resume Checker
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            75% of resumes are rejected by ATS before a human sees them.
            Check your score instantly — no signup required.
          </p>
        </div>

        {/* Form Section */}
        {!result && (
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-4xl mx-auto">
            <form onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Resume Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Resume Text
                    <span className="text-gray-400 font-normal ml-2">
                      (Copy & paste from your resume)
                    </span>
                  </label>
                  <textarea
                    value={resumeText}
                    onChange={(e) => setResumeText(e.target.value)}
                    placeholder="Paste your resume content here...

Example:
John Smith
Software Engineer

EXPERIENCE
Senior Developer at Tech Corp (2020-Present)
• Built scalable APIs serving 1M+ users
• Led team of 5 engineers..."
                    className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {resumeText.length} characters (min 100)
                  </p>
                </div>

                {/* Job Description Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Description
                    <span className="text-gray-400 font-normal ml-2">
                      (Copy from job posting)
                    </span>
                  </label>
                  <textarea
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder="Paste the job description here...

Example:
We're looking for a Senior Software Engineer to join our team. You will:
• Design and build scalable backend systems
• Collaborate with product teams
• Mentor junior developers

Requirements:
• 5+ years of experience
• Python, Node.js, AWS
• Strong communication skills"
                    className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {jobDescription.length} characters (min 50)
                  </p>
                </div>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Analyzing your resume...
                  </>
                ) : (
                  <>
                    Check ATS Score — Free
                    <span className="text-white/60">(5 checks/hour)</span>
                  </>
                )}
              </button>
            </form>

            {/* Trust indicators */}
            <div className="mt-6 pt-6 border-t border-gray-100 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                No signup required
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Instant results
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                Data not stored
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {result && (
          <div className="max-w-4xl mx-auto">
            {/* Score Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Your ATS Score
                </h2>
                <p className="text-gray-600">{result.summary}</p>
              </div>

              {/* Main Score */}
              <div className="flex justify-center mb-8">
                <div className="relative">
                  <svg className="w-40 h-40 transform -rotate-90">
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      className="text-gray-100"
                    />
                    <circle
                      cx="80"
                      cy="80"
                      r="70"
                      stroke="currentColor"
                      strokeWidth="12"
                      fill="none"
                      strokeDasharray={439.8}
                      strokeDashoffset={439.8 - (439.8 * result.atsScore) / 100}
                      className={
                        result.atsScore >= 80
                          ? 'text-green-500'
                          : result.atsScore >= 60
                          ? 'text-yellow-500'
                          : 'text-red-500'
                      }
                      strokeLinecap="round"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span
                      className={`text-5xl font-bold ${
                        result.atsScore >= 80
                          ? 'text-green-500'
                          : result.atsScore >= 60
                          ? 'text-yellow-500'
                          : 'text-red-500'
                      }`}
                    >
                      {result.atsScore}
                    </span>
                    <span className="text-gray-500 text-sm">out of 100</span>
                  </div>
                </div>
              </div>

              {/* Score Breakdown */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <ScoreGauge
                  score={result.scoreBreakdown.keywordMatch}
                  label="Keyword Match"
                />
                <ScoreGauge
                  score={result.scoreBreakdown.formatting}
                  label="Formatting"
                />
                <ScoreGauge
                  score={result.scoreBreakdown.relevance}
                  label="Relevance"
                />
              </div>

              {/* Keywords */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                {/* Matched Keywords */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                      ✓
                    </span>
                    Matched Keywords ({result.matchedKeywords.length})
                  </h3>
                  <div>
                    {result.matchedKeywords.length > 0 ? (
                      result.matchedKeywords.map((keyword, i) => (
                        <KeywordBadge key={i} keyword={keyword} type="matched" />
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">No significant keyword matches found</p>
                    )}
                  </div>
                </div>

                {/* Missing Keywords */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center text-red-600">
                      !
                    </span>
                    Missing Keywords ({result.missingKeywords.length})
                  </h3>
                  <div>
                    {result.missingKeywords.length > 0 ? (
                      result.missingKeywords.map((keyword, i) => (
                        <KeywordBadge key={i} keyword={keyword} type="missing" />
                      ))
                    ) : (
                      <p className="text-gray-500 text-sm">Great job! No critical keywords missing</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Suggestions */}
              {result.suggestions.length > 0 && (
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Suggestions to Improve Your Score
                  </h3>
                  <ul className="space-y-2">
                    {result.suggestions.map((suggestion, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-700">
                        <span className="text-blue-500 mt-1">→</span>
                        {suggestion}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* CTA Card */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl shadow-xl p-8 text-center text-white">
              <h3 className="text-2xl font-bold mb-3">
                Want to improve your score to 90%+?
              </h3>
              <p className="text-white/80 mb-6 max-w-xl mx-auto">
                Kwikly AI automatically tailors your resume to match job descriptions,
                adds missing keywords naturally, and generates ATS-optimized versions in 60 seconds.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/register"
                  className="px-8 py-4 bg-white text-primary-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
                >
                  Get Your Tailored Resume — Free
                </a>
                <button
                  onClick={() => {
                    setResult(null);
                    setResumeText('');
                    setJobDescription('');
                  }}
                  className="px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/20"
                >
                  Check Another Resume
                </button>
              </div>
              <p className="text-white/60 text-sm mt-4">
                No credit card required • Free: 3 jobs/week, 2 tailors/month
              </p>
            </div>
          </div>
        )}

        {/* How it works section */}
        {!result && (
          <div className="mt-16 text-white">
            <h2 className="text-2xl font-bold text-center mb-8">
              How ATS Systems Work (And Why You&apos;re Getting Filtered)
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/10">
                <div className="text-3xl mb-4">1️⃣</div>
                <h3 className="font-semibold mb-2">Keyword Scanning</h3>
                <p className="text-white/70 text-sm">
                  ATS systems scan for specific keywords from the job description.
                  If your resume doesn&apos;t match, you&apos;re auto-rejected.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/10">
                <div className="text-3xl mb-4">2️⃣</div>
                <h3 className="font-semibold mb-2">Format Parsing</h3>
                <p className="text-white/70 text-sm">
                  Tables, graphics, and fancy formatting break ATS parsers.
                  Clean, simple formats score higher.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/10">
                <div className="text-3xl mb-4">3️⃣</div>
                <h3 className="font-semibold mb-2">Relevance Scoring</h3>
                <p className="text-white/70 text-sm">
                  Resumes are ranked by relevance to the job. Generic resumes
                  score low even with the right skills.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-16">
        <div className="max-w-6xl mx-auto px-4 py-8 flex flex-col sm:flex-row items-center justify-between text-white/60 text-sm">
          <p>© 2026 Kwikly. All rights reserved.</p>
          <div className="flex gap-6 mt-4 sm:mt-0">
            <a href="/privacy" className="hover:text-white transition-colors">
              Privacy
            </a>
            <a href="/support" className="hover:text-white transition-colors">
              Support
            </a>
            <a href="/help" className="hover:text-white transition-colors">
              Help
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ATSCheckerPage;
